import Cookie, { type CookieSetOptions } from 'universal-cookie';
import { apiEndpoint, prefix, url } from './constants';
import _ from 'lodash';
import dayjs from 'dayjs';
import type { DayInteractionSummary } from '@app/services/interaction.types';

const cookie = new Cookie();

type CookieOptions = CookieSetOptions & {
    lifespan: number;
};

const utils = {
    /**
     * Returns a prefixed key
     */
    getPrefixedKey(key: string) {
        return prefix + key;
    },

    /** COOKIES */

    deleteCookie(key: string) {
        return cookie.remove(key, { path: '/' });
    },

    getCookie(key: string) {
        return cookie.get(key);
    },

    saveCookie(key: string, value: Cookie, options: CookieSetOptions) {
        return cookie.set(key, value, options);
    },

    /** LOCAL STORAGE */

    storeData(
        key: string,
        data: string | object,
        options: CookieOptions = {
            lifespan: 0, // in seconds
        }
    ) {
        let expiresAt = null;
        if (options.lifespan > 0) {
            expiresAt = dayjs().add(options.lifespan, 'second');
        }
        const item = {
            data: data,
            expiresAt: expiresAt ? expiresAt.toLocaleString() : null,
        };

        localStorage.setItem(this.getPrefixedKey(key), JSON.stringify(item));
    },

    retrieveData(key: string) {
        const jsonStringItem = localStorage.getItem(this.getPrefixedKey(key));
        if (!jsonStringItem) {
            return null;
        }
        const item = JSON.parse(jsonStringItem);
        if (!item.expiresAt) {
            return item.data;
        }

        return item.data;
    },

    clearData(key: string) {
        localStorage.removeItem(this.getPrefixedKey(key));
    },

    clearAllData() {
        const storageItems = Object.keys(localStorage).filter((x) => x.startsWith(prefix));
        if (storageItems.length) {
            storageItems.forEach((key) => localStorage.removeItem(key));
        }
    },

    getTokenExpiry(tokenKey: string) {
        const jsonStringItem = localStorage.getItem(this.getPrefixedKey(tokenKey));
        if (!jsonStringItem) {
            return null;
        }
        const item = JSON.parse(jsonStringItem);
        return item?.expiresAt ? dayjs(item.expiresAt).valueOf() : null;
    },

    storeAccessToken(token: string, options: CookieOptions) {
        this.clearData('challenge_token');
        this.storeData('access_token', token, options);
    },

    getAccessToken() {
        return this.retrieveData('access_token');
    },

    getAccessTokenExpiry() {
        return this.getTokenExpiry('access_token');
    },

    getChallengeToken() {
        return this.retrieveData('challenge_token');
    },

    storeRefreshToken(token: string, options: CookieOptions) {
        this.storeData('refresh_token', token, options);
    },

    getRefreshToken() {
        return this.retrieveData('refresh_token');
    },

    storeIdToken(token: string, options: CookieOptions) {
        this.storeData('id_token', token, options);
    },

    getIdToken() {
        return this.retrieveData('id_token');
    },

    storeMeData(meData: string | object) {
        this.storeData('me_data', meData);
    },

    getMeData() {
        return this.retrieveData('me_data');
    },

    storeImpersonationToken(token: string, options: CookieOptions) {
        this.storeData('impersonation_token', token, options);
    },

    getImpersonationToken() {
        const item = localStorage.getItem(this.getPrefixedKey('impersonation_token'));
        if (!item) return null;
        const { data, expiresAt } = JSON.parse(item);
        if (dayjs(expiresAt).isBefore(dayjs())) {
            this.clearData('impersonation_token');
            return null;
        }
        return data;
    },

    getImpersonationTokenExpiry() {
        return this.getTokenExpiry('impersonation_token');
    },

    /**
     * Builds a URL query string from a given object
     */
    buildUrlQuery(url = '', params = {}, start = '?') {
        if (_.isEmpty(params)) {
            return url;
        }
        // remove undefined and null entries
        const cleanedParams = _.pickBy(params, (v) => v !== undefined && v !== null);
        const queryString = new URLSearchParams(cleanedParams as never).toString();
        return url + start + queryString;
    },

    /**
     * Checks if the build is production
     */
    isProduction() {
        return import.meta.env.NODE_ENV === 'production';
    },

    /**
     * Checks if user logged in or not
     */
    isLoggedIn() {
        if (this.getAccessToken()) {
            return true;
        }

        return false;
    },

    checkPath(pathname: string, path: string) {
        return (
            //@ts-expect-error: trim is not defined on string
            pathname.trim(/(?:^\/)|(?:\/$)/g, '').indexOf(path.trim(/(?:^\/)|(?:\/$)/g, '')) > -1
        );
    },

    /**
     * Checks if path is current route path
     */
    hasRoutePath(path: string) {
        const pathname = location.pathname;
        let hasPath = false;
        if (this.checkPath(pathname, path)) {
            hasPath = true;
        }

        return hasPath;
    },

    /**
     * Checks if the current route is an auth route
     */
    isAuthRoute() {
        const pathname = location.pathname;
        const authRoutes = { ...url.account };
        let isAuthRoute = false;
        for (const prop in authRoutes) {
            const path = authRoutes[prop as keyof typeof authRoutes];
            if (this.checkPath(pathname, path)) {
                isAuthRoute = true;
                break;
            }
        }

        return isAuthRoute;
    },

    /**
     * Checks if the current route contains a given path
     */
    currentRouteContainsPath(path: string) {
        return path != '#' && window.location.href.replace('#', '').indexOf(path) > -1;
    },

    /**
     * Formats a date string to a given format
     */
    formatDate(dateString: string, format = 'DD/MM/YYYY') {
        return dayjs(new Date(dateString)).format(format);
    },

    /**
     * Copies a given text to the clipboard
     */
    async copyToClipboard(text: string) {
        if (!navigator.clipboard) {
            return false;
        }

        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            alert('Could not copy');
            return false;
        }
    },

    /**
     * Downloads a file from a given url
     */
    downloadFile({ href, fileName }: { href: string; fileName?: string }) {
        const a = document.createElement('a');
        a.download = fileName || '';
        a.href = href;
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        });
        a.dispatchEvent(clickEvt);
        a.remove();
    },

    /**
     * Downloads a file from a given Blob
     */
    async downloadFileFromBlob({ blob, fileName }: { blob: Blob; fileName?: string }) {
        // Show Save As dialog if available
        if ('showSaveFilePicker' in window) {
            try {
                const handle = await showSaveFilePicker({
                    suggestedName: fileName, // define default filename
                    startIn: 'downloads', // define default directory
                });
                const writable = await handle.createWritable();
                await writable.write(blob);
                await writable.close();
            } catch {
                // Silently exit on error
            }
            return;
        }

        // Use a download link as a fallback
        const href = URL.createObjectURL(blob);
        this.downloadFile({ href, fileName });
        URL.revokeObjectURL(href);
    },

    getAbsoluteFileUrl(filePath = '') {
        return apiEndpoint + filePath;
    },

    getLanguageName(languageCode: string, currentLanguageCode = 'en') {
        const languageNames = new Intl.DisplayNames([currentLanguageCode], { type: 'language' });
        return languageNames.of(languageCode);
    },

    secondsToMinutesSeconds(totalSeconds: number) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}m ${seconds}s`;
    },

    formatTimeToDoubleDigits(millisecs: number) {
        const hours = Math.floor((millisecs / (1000 * 60 * 60)) % 24)
            .toString()
            .padStart(2, '0');
        const mins = Math.floor((millisecs / (1000 * 60)) % 60)
            .toString()
            .padStart(2, '0');
        const secs = Math.floor((millisecs / 1000) % 60)
            .toString()
            .padStart(2, '0');
        return `${hours}:${mins}:${secs}`;
    },

    formateDateToYMD(date: Date) {
        const yyyy = date.getFullYear();
        const mm = (date.getMonth() + 1).toString().padStart(2, '0');
        const dd = date.getDate().toString().padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    },

    generateTimeSeriesData(
        days: number,
        interactions: DayInteractionSummary[],
        startDate = Date.now()
    ) {
        const typeMap: Record<string, 'calls' | 'emails' | 'whatsapp' | 'liveChat'> = {
            Call: 'calls',
            Email: 'emails',
            Whatsapp: 'whatsapp',
            'Live-chat': 'liveChat',
        };
        const data = [];
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(startDate - i * 24 * 60 * 60 * 1000);
            const dateStr = date.toLocaleDateString('en-GB', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });

            const dayInteractions = interactions.filter((int) => {
                const intDateStr = new Date(int.date).toLocaleDateString('en-GB', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                });
                return intDateStr === dateStr;
            });

            const totalsForDay = dayInteractions.reduce(
                (acc, intTypeSummary) => {
                    const type = intTypeSummary.type;
                    const mappedType = typeMap[type];
                    if (mappedType) {
                        acc[mappedType] += intTypeSummary.total;
                        acc.total += intTypeSummary.total;
                    }
                    return acc;
                },
                {
                    date: date.toLocaleDateString('en-GB', {
                        month: 'short',
                        day: 'numeric',
                        year: '2-digit',
                    }),
                    total: 0,
                    calls: 0,
                    emails: 0,
                    whatsapp: 0,
                    liveChat: 0,
                }
            );
            data.push(totalsForDay);
        }
        return data;
    },

    capitaliseWord(word: string) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    },

    getTableOrderDir(field?: string, order?: 'ascend' | 'descend') {
        if (field && typeof order === 'string') {
            return order === 'ascend' ? 'asc' : 'desc';
        }
        return undefined;
    },
};

export default utils;
